import { Link, LiveProduct, Loading, SearchIcon } from '@/components';
import { CloseIcon } from '@/components/icons/Close';
import { Icon } from '@/components/ui/Icon';
import { useFetchLiveVideosQuery } from '@/features/livestream/detail/api';
import { LiveVideo } from '@/features/livestream/shared';
import { PagePath } from '@/layouts/shared';
import { createHandle, debounce } from '@/libs/app';
import { AnimatePresence, motion } from 'framer-motion';
import { TFunction } from 'next-i18next';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';

const SearchPartial = ({ t }: { t: TFunction }) => {
  const [dataSearchKey, setDataSearchKey] = useState<LiveVideo[]>([]);
  const [keyword, setKeyword] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);

  const { refetch: refetchKeyword, isLoading: loadingSearch } = useFetchLiveVideosQuery(
    { product_keyword: keyword },
    {
      enabled: false,
      retry: false,
      refetchOnWindowFocus: false,
      onError: ({ message }) => {
        toast.error(message);
      },
      onSuccess: ({ data }) => {
        let _data: any = [];
        _data = data.map((d) => {
          let _d: any = {};
          let productData: any = d.live_stream_product.find(
            (p) => p.products.product_code.toLocaleLowerCase() === keyword.toLocaleLowerCase()
          );
          _d = {
            ...productData,
            link: d.link,
            live_stream: {
              images: d.images,
              name: d.live_streamer.name,
              id: d.id + '',
              title: d.name,
              duration: d.duration,
            },
          };
          return _d;
        });
        if (keyword) {
          setDataSearchKey(Array.isArray(_data) ? _data : []);
        }
      },
    }
  );
  useEffect(() => {
    if (keyword !== '') {
      refetchKeyword();
    } else {
      setDataSearchKey([]);
    }
  }, [keyword, refetchKeyword]);
  const handleChange = (value: string) => {
    setKeyword(value);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const optimizedFn = useCallback(debounce(handleChange, 300), []);
  const clearInput = () => {
    if (inputRef.current) {
      inputRef.current.value = '';
      setKeyword('');
    }
  };
  return (
    <div className="tw-w-full tw-relative">
      <div className="tw-flex tw-rounded-5 tw-text-secondary-70 tw-border tw-overflow-hidden tw-border-primary-main tw-py-2 tw-px-3 ">
        <Icon className="tw-w-6 tw-h-6" icon={<SearchIcon />}></Icon>
        <input
          type="text"
          ref={inputRef}
          placeholder={t('layout:label.search-keyword')}
          className="tw-w-full tw-outline-none tw-ml-2 tw-placeholder-secondary-70 "
          onChange={(e) => optimizedFn(e.target.value)}
        />
        {keyword && (
          <span className="tw-text-primary-main tw-cursor-pointer" onClick={clearInput}>
            <Icon icon={<CloseIcon />} className="tw-w-6 tw-h-6" />
          </span>
        )}
      </div>
      <AnimatePresence>
        {keyword && (
          <motion.div
            className="tw-absolute  tw-drop-shadow tw-px-2 md:tw-px-4 tw-py-2 tw-left-0 tw-z-10 tw-bg-white tw-rounded-5 top-full-8 tw-w-full"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className="tw-w-full tw-h-full tw-overflow-y-scroll tw-max-h-80">
              {loadingSearch ? (
                <Loading />
              ) : !dataSearchKey.length ? (
                <div className="tw-py-10 tw-text-center">Không tìm thấy video</div>
              ) : (
                dataSearchKey.map((data: any) => {
                  const id = data.live_stream.id;
                  let handle = `${createHandle(data.live_stream.title)}-${id}`;
                  return (
                    <div className="tw-py-2 tw-border-b" key={'live' + id}>
                      <div className="tw-w-full md:tw-w-2/3" onClick={() => clearInput()}>
                        <Link
                          url={{ pathname: PagePath.LiveVideoPage, query: { id: handle, start: data.played } }}
                          className="tw-w-full"
                        >
                          <div className="tw-py-2 tw-px-4 tw-rounded-5">
                            <LiveProduct data={data} />
                          </div>
                        </Link>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
export default memo(SearchPartial);
